/**
 * 인라인 메모 표시 컴포넌트
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  InlineStickyNote,
  InlineTitleAndColorRow,
  InlineStickyNoteTitle,
  InlineColorSelector,
  InlineColorButton,
  InlineStickyNoteDivider,
  InlineStickyNoteContent,
} from '../styles';

interface InlineStickyNoteWrapperProps {
  annotationId: string;
  color: 'green' | 'yellow' | 'blue';
  noteTitle: string;
  noteContent: string;
  onViewNote: () => void;
}

/**
 * 텍스트를 URL과 일반 텍스트로 분리하여 React 요소 배열로 변환
 */
export const parseTextWithLinks = (text: string): React.ReactNode[] => {
  if (!text || typeof text !== 'string') {
    return [text];
  }
  
  // URL 패턴: http://, https://, www.로 시작하는 URL
  const urlPattern = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
  
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let keyCounter = 0;
  
  // 모든 매칭 찾기
  const matches: Array<{ index: number; url: string }> = [];
  let match;
  const regex = new RegExp(urlPattern.source, urlPattern.flags);
  
  while ((match = regex.exec(text)) !== null) {
    if (match.index !== undefined) {
      matches.push({
        index: match.index,
        url: match[0],
      });
    }
    // 무한 루프 방지
    if (match.index === regex.lastIndex) {
      regex.lastIndex++;
    }
  }
  
  // 매칭이 없으면 원본 텍스트 반환
  if (matches.length === 0) {
    return [text];
  }
  
  // 각 매칭을 처리
  matches.forEach((matchItem) => {
    // URL 이전의 일반 텍스트
    if (matchItem.index > lastIndex) {
      const beforeText = text.substring(lastIndex, matchItem.index);
      if (beforeText) {
        parts.push(<React.Fragment key={`text-${keyCounter++}`}>{beforeText}</React.Fragment>);
      }
    }
    
    // URL 처리
    let url = matchItem.url;
    let href = url;
    
    if (url.toLowerCase().startsWith('www.')) {
      href = `https://${url}`;
    }
    
    // URL 끝의 구두점 제거
    const cleanUrl = url.replace(/[.,;:!?]+$/, '');
    const cleanHref = href.replace(/[.,;:!?]+$/, '');
    const trailingPunctuation = url.slice(cleanUrl.length);
    
    parts.push(
      <a
        key={`link-${keyCounter++}`}
        href={cleanHref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          e.stopPropagation();
        }}
        style={{
          color: '#0066cc',
          textDecoration: 'underline',
          cursor: 'pointer',
        }}
      >
        {cleanUrl}
      </a>
    );
    
    // URL 뒤의 구두점
    if (trailingPunctuation) {
      parts.push(<React.Fragment key={`punct-${keyCounter++}`}>{trailingPunctuation}</React.Fragment>);
    }
    
    lastIndex = matchItem.index + matchItem.url.length;
  });
  
  // 마지막 URL 이후의 일반 텍스트
  if (lastIndex < text.length) {
    const afterText = text.substring(lastIndex);
    if (afterText) {
      parts.push(<React.Fragment key={`text-after-${keyCounter++}`}>{afterText}</React.Fragment>);
    }
  }
  
  return parts.length > 0 ? parts : [text];
};

const InlineStickyNoteWrapper: React.FC<InlineStickyNoteWrapperProps> = ({
  annotationId,
  color,
  noteTitle,
  noteContent,
  onViewNote,
}) => {
  const noteRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768 || 'ontouchstart' in window);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 호버 시 메모창 표시 로직 주석처리 (향후 복구 예정)
  // useEffect(() => {
  //   const parentElement = noteRef.current?.parentElement;
  //   if (!parentElement) return;

  //   const handleParentMouseEnter = () => {
  //     if (!isMobile) {
  //       setIsVisible(true);
  //     }
  //   };

  //   const handleParentMouseLeave = () => {
  //     if (!isMobile) {
  //       setIsVisible(false);
  //     }
  //   };

  //   parentElement.addEventListener('mouseenter', handleParentMouseEnter);
  //   parentElement.addEventListener('mouseleave', handleParentMouseLeave);
    
  //   const handleParentClick = (e: MouseEvent) => {
  //     if (isMobile) {
  //       e.preventDefault();
  //       e.stopPropagation();
  //       setIsVisible(prev => !prev);
  //     }
  //   };
    
  //   if (isMobile) {
  //     parentElement.addEventListener('click', handleParentClick);
  //   }

  //   return () => {
  //     parentElement.removeEventListener('mouseenter', handleParentMouseEnter);
  //     parentElement.removeEventListener('mouseleave', handleParentMouseLeave);
  //     if (isMobile) {
  //       parentElement.removeEventListener('click', handleParentClick);
  //     }
  //   };
  // }, [isMobile]);

  if (!noteTitle && !noteContent) return null;

  return (
    <InlineStickyNote
      ref={noteRef}
      data-inline-note="true"
      $color={color}
      $isVisible={false}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onViewNote();
      }}
    >
      <InlineTitleAndColorRow>
        <InlineStickyNoteTitle>{noteTitle || '메모'}</InlineStickyNoteTitle>
        <InlineColorSelector>
          <InlineColorButton $color="green" />
          <InlineColorButton $color="yellow" />
          <InlineColorButton $color="blue" />
        </InlineColorSelector>
      </InlineTitleAndColorRow>
      {noteContent && (
        <>
          <InlineStickyNoteDivider />
          <InlineStickyNoteContent>
            {parseTextWithLinks(noteContent).map((part, index) => {
              // React 요소인 경우 그대로 반환
              if (React.isValidElement(part)) {
                return part;
              }
              // 문자열인 경우 그대로 반환
              return <React.Fragment key={`part-${index}`}>{part}</React.Fragment>;
            })}
          </InlineStickyNoteContent>
        </>
      )}
    </InlineStickyNote>
  );
};

export default InlineStickyNoteWrapper;
